import { useCallback, useEffect, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import {
    RefreshCw, ChevronLeft, ChevronRight,
    Download, Upload, FileText, AlertCircle, CheckCircle,
} from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import FilterPills from "./admin/FilterPills";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

const PAGE_SIZE = 25;
const RELABEL_BULK_CHUNK = 500;
// BE caps page_size at 200 — fetch the full export in these chunks.
const RELABEL_EXPORT_CHUNK = 200;

// Relabel XLSX columns. `action` + `id` lead so a Download-Current export
// round-trips: blank action = add, `available`/`used` flip an existing row
// by id. The three data columns are exactly what the operator's source
// spreadsheet carries (PO / Original Barcode / New Barcode).
const RELABEL_HEADER = [
    "action", "id", "po_number", "original_barcode", "new_barcode", "status",
];

// Identifier-style columns forced to Text on export so Excel keeps long
// barcode digit strings verbatim instead of mangling them to scientific
// notation on round-trip.
const TEXT_COLUMNS = new Set<string>([
    "id", "po_number", "original_barcode", "new_barcode",
]);

const cellToString = (cell: ExcelJS.Cell | undefined): string => {
    if (!cell) return "";
    const v = cell.value;
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === "object") {
        if ("result" in v && (v as { result?: unknown }).result != null) {
            return String((v as { result: unknown }).result);
        }
        if ("text" in v && (v as { text?: unknown }).text != null) {
            return String((v as { text: unknown }).text);
        }
        if ("richText" in v) {
            const rt = (v as { richText?: { text: string }[] }).richText;
            return Array.isArray(rt) ? rt.map(r => r.text).join("") : "";
        }
    }
    return String(v);
};

const downloadXlsx = async (filename: string, header: readonly string[], rows: string[][]) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Relabel");
    ws.addRow(header as string[]);
    for (const r of rows) ws.addRow(r);
    header.forEach((name, i) => {
        if (TEXT_COLUMNS.has(name)) ws.getColumn(i + 1).numFmt = "@";
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const readXlsxRows = async (file: File): Promise<{ header: string[]; rows: string[][] }> => {
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.worksheets[0];
    if (!ws || ws.rowCount < 1) return { header: [], rows: [] };
    const headerRow = ws.getRow(1);
    const colCount = headerRow.cellCount;
    const header: string[] = [];
    for (let c = 1; c <= colCount; c++) {
        header[c - 1] = cellToString(headerRow.getCell(c)).trim().toLowerCase();
    }
    const rows: string[][] = [];
    for (let r = 2; r <= ws.rowCount; r++) {
        const xrow = ws.getRow(r);
        const cells: string[] = [];
        for (let c = 1; c <= colCount; c++) cells[c - 1] = cellToString(xrow.getCell(c));
        if (cells.some(s => s.trim() !== "")) rows.push(cells);
    }
    return { header, rows };
};

interface BulkResult {
    added: number;
    updated: number;
    errors: { row: number; reason: string }[];
}

interface RelabelRow {
    id: number;
    po_number: string;
    original_barcode: string;
    new_barcode: string;
    status: "available" | "used";
    printed_at?: string | null;
    created_at: string;
    updated_at: string;
}

// Relabel admin: lists the PO / original-barcode / new-barcode pairings,
// imports them from an XLSX upload, exports current / template, and lets
// admins flip any row between available (green) and used (red) with a click.
const RelabelAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [rows, setRows] = useState<RelabelRow[]>([]);
    const [total, setTotal] = useState(0);
    // "" = all, else "available" / "used".
    const [statusFilter, setStatusFilter] = useState<string>("");
    // Free-text search — BE matches across po_number / original_barcode /
    // new_barcode. Commits on Enter / blur so a fast typist doesn't hit the
    // BE per keystroke. `?search=` URL param lets AdminSearch deep-link in.
    const [searchFilter, setSearchFilter] = useUrlSearchParam("search");
    const [searchInput, setSearchInput] = useState(searchFilter);
    useEffect(() => { setSearchInput(searchFilter); }, [searchFilter]);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [togglingId, setTogglingId] = useState<number | null>(null);

    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
    const [uploadResult, setUploadResult] = useState<BulkResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => { setPage(0); }, [statusFilter, searchFilter]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set("status", statusFilter);
            if (searchFilter.trim()) params.set("q", searchFilter.trim());
            params.set("page", String(page));
            params.set("page_size", String(PAGE_SIZE));
            const res = await apiFetch(`/relabel?${params}`, {});
            if (res.ok) {
                const data = await res.json();
                setRows(Array.isArray(data.relabel) ? data.relabel : []);
                setTotal(typeof data.total === "number" ? data.total : 0);
            }
        } catch {
            toast.error(t("server_error"), { toastId: "relabel-load-err" });
        } finally {
            setLoading(false);
        }
    }, [statusFilter, searchFilter, page, t]);

    useEffect(() => { refresh(); }, [refresh]);

    const toggleStatus = async (row: RelabelRow) => {
        const next = row.status === "available" ? "used" : "available";
        setTogglingId(row.id);
        try {
            const res = await apiFetch(`/relabel/status`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: row.id, status: next }),
            });
            if (!res.ok) throw new Error("status");
            // Optimistic local update — avoids a full refetch (and keeps the
            // row in place even under an active status filter until refresh).
            setRows(prev => prev.map(r => (r.id === row.id ? { ...r, status: next } : r)));
        } catch {
            toast.error(t("server_error"), { toastId: "relabel-toggle-err" });
        } finally {
            setTogglingId(null);
        }
    };

    const downloadTemplate = async () => {
        const example: string[][] = [
            // add rows: action blank (defaults to add), id blank. Same original
            // barcode twice with different new barcodes shows the many-to-one
            // dispense order (FIFO by upload order).
            ["", "", "PO-2026-001", "OLD-BARCODE-A", "NEW-BARCODE-1", ""],
            ["", "", "PO-2026-001", "OLD-BARCODE-A", "NEW-BARCODE-2", ""],
            ["", "", "PO-2026-001", "OLD-BARCODE-B", "NEW-BARCODE-3", ""],
            // flip an existing row (id from a Download-Current export).
            ["used",      "12345", "", "", "", ""],
            ["available", "67890", "", "", "", ""],
        ];
        await downloadXlsx("relabel-template.xlsx", RELABEL_HEADER, example);
    };

    // Export EVERY row matching the current filters, not just the page on
    // screen. Pages through the BE (which caps page_size at 200) until the
    // reported total is collected, then writes one XLSX.
    const downloadCurrent = async () => {
        setDownloading(true);
        try {
            const all: RelabelRow[] = [];
            let page = 0;
            let expected = Infinity;
            do {
                const params = new URLSearchParams();
                if (statusFilter) params.set("status", statusFilter);
                if (searchFilter.trim()) params.set("q", searchFilter.trim());
                params.set("page", String(page));
                params.set("page_size", String(RELABEL_EXPORT_CHUNK));
                const res = await apiFetch(`/relabel?${params}`, {});
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const batch: RelabelRow[] = Array.isArray(data.relabel) ? data.relabel : [];
                all.push(...batch);
                expected = typeof data.total === "number" ? data.total : all.length;
                page += 1;
                // Guard against a short/empty batch so we never loop forever.
                if (batch.length === 0) break;
            } while (all.length < expected);

            const out: string[][] = all.map(r => [
                "", // action — blank for current export; user fills in for re-upload
                String(r.id),
                r.po_number,
                r.original_barcode,
                r.new_barcode,
                r.status,
            ]);
            await downloadXlsx("relabel-current.xlsx", RELABEL_HEADER, out);
        } catch {
            toast.error(t("server_error"), { toastId: "relabel-download-err" });
        } finally {
            setDownloading(false);
        }
    };

    const onFileChosen = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const file = ev.target.files?.[0];
        if (!file) return;
        ev.target.value = "";

        let parsed: { header: string[]; rows: string[][] };
        try {
            parsed = await readXlsxRows(file);
        } catch {
            toast.error(t("relabel_upload_parse_error"), { toastId: "relabel-upload-parse" });
            return;
        }
        const { header, rows: dataRows } = parsed;
        if (dataRows.length === 0) {
            toast.error(t("relabel_upload_empty"), { toastId: "relabel-upload-empty" });
            return;
        }
        const idx = (name: string) => header.indexOf(name);
        // Accept the operator's friendly headers ("Original Barcode") which
        // lower-case to "original_barcode" only if they used underscores;
        // fall back to space-separated variants so a hand-made sheet works.
        const origIdx = idx("original_barcode") !== -1 ? idx("original_barcode") : idx("original barcode");
        const newIdx = idx("new_barcode") !== -1 ? idx("new_barcode") : idx("new barcode");
        const poIdx = idx("po_number") !== -1 ? idx("po_number") : idx("po");
        if (origIdx === -1 || newIdx === -1) {
            toast.error(t("relabel_upload_bad_header"), { toastId: "relabel-upload-hdr" });
            return;
        }

        const cell = (r: string[], i: number) => (i >= 0 ? (r[i] ?? "") : "");
        const rowsToSend = dataRows.map(r => ({
            action:           cell(r, idx("action")),
            id:               cell(r, idx("id")),
            po_number:        cell(r, poIdx),
            original_barcode: cell(r, origIdx),
            new_barcode:      cell(r, newIdx),
        }));

        setUploading(true);
        setUploadProgress({ done: 0, total: rowsToSend.length });
        setUploadResult(null);

        const combined: BulkResult = { added: 0, updated: 0, errors: [] };
        try {
            for (let i = 0; i < rowsToSend.length; i += RELABEL_BULK_CHUNK) {
                const chunk = rowsToSend.slice(i, i + RELABEL_BULK_CHUNK);
                const res = await apiFetch(`/relabel/bulk`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ rows: chunk }),
                });
                if (res.status === 413) {
                    toast.error(t("bulk_upload_too_large"), { toastId: "relabel-upload-413" });
                    break;
                }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const result: BulkResult = await res.json();
                combined.added += result.added;
                combined.updated += result.updated;
                for (const e of result.errors) {
                    combined.errors.push({ row: e.row + i, reason: e.reason });
                }
                setUploadProgress({
                    done: Math.min(i + chunk.length, rowsToSend.length),
                    total: rowsToSend.length,
                });
            }
            setUploadResult(combined);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "relabel-upload-err" });
        } finally {
            setUploading(false);
            setUploadProgress(null);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(page, Math.max(totalPages - 1, 0));
    const rangeFrom = total === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeTo = Math.min((safePage + 1) * PAGE_SIZE, total);

    const statusOptions = [
        { value: "available", label: t("relabel_status_available") },
        { value: "used", label: t("relabel_status_used") },
    ];

    return (
        <div className="inventory-admin">
            <div className="totes-admin-actions">
                <button type="button" className="btn btn-ghost" onClick={downloadTemplate}>
                    <FileText size={16} /> {t("relabel_download_template")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={downloadCurrent}
                    disabled={loading || downloading || total === 0}
                >
                    <Download size={16} /> {downloading ? t("submitting") : t("relabel_download_current")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                >
                    <Upload size={16} /> {uploading ? t("submitting") : t("relabel_upload_xlsx")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={refresh}
                    disabled={loading}
                    style={{ marginLeft: "auto" }}
                >
                    <RefreshCw size={14} /> {t("refresh")}
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: "none" }}
                    onChange={onFileChosen}
                />
            </div>

            {uploadProgress && (
                <div className="totes-admin-progress">
                    <div className="totes-admin-progress-label">
                        {t("totes_upload_progress", {
                            done: String(uploadProgress.done),
                            total: String(uploadProgress.total),
                        })}
                        <span className="totes-admin-progress-pct">
                            {Math.round((uploadProgress.done / Math.max(uploadProgress.total, 1)) * 100)}%
                        </span>
                    </div>
                    <div className="totes-admin-progress-track">
                        <div
                            className="totes-admin-progress-fill"
                            style={{ width: `${(uploadProgress.done / Math.max(uploadProgress.total, 1)) * 100}%` }}
                        />
                    </div>
                </div>
            )}

            {uploadResult && (
                <div className="totes-admin-result">
                    <div className="totes-admin-result-line">
                        <CheckCircle strokeWidth={3} size={16} className="totes-admin-result-ok" />
                        <span>
                            {t("relabel_bulk_summary", {
                                added: String(uploadResult.added),
                                updated: String(uploadResult.updated),
                            })}
                        </span>
                    </div>
                    {uploadResult.errors.length > 0 && (
                        <ul className="totes-admin-errors">
                            {uploadResult.errors.map((e, i) => (
                                <li key={i}>
                                    <AlertCircle size={14} />
                                    <span>{t("totes_row_label", { row: String(e.row) })}: {e.reason}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <FilterPills
                className="warehouse-admin-pills"
                value={statusFilter}
                onChange={setStatusFilter}
                options={statusOptions}
            />

            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={t("relabel_filter_search_hint")}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setSearchFilter(searchInput.trim());
                        }
                    }}
                    onBlur={() => {
                        if (searchInput.trim() !== searchFilter.trim()) {
                            setSearchFilter(searchInput.trim());
                        }
                    }}
                />
            </div>

            <div className="locations-table-wrap">
                <table className="locations-table">
                    <thead>
                        <tr>
                            <th className="locations-table-num">{t("relabel_col_id")}</th>
                            <th>{t("relabel_col_po")}</th>
                            <th>{t("relabel_col_original")}</th>
                            <th>{t("relabel_col_new")}</th>
                            <th>{t("relabel_col_status")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.id}>
                                <td className="locations-table-num locations-table-mono">{r.id}</td>
                                <td>{r.po_number}</td>
                                <td className="locations-table-mono">{r.original_barcode}</td>
                                <td className="locations-table-mono">{r.new_barcode}</td>
                                <td>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${r.status === "available" ? "btn-success" : "btn-danger"}`}
                                        onClick={() => toggleStatus(r)}
                                        disabled={togglingId === r.id || readOnly}
                                        aria-disabled={readOnly}
                                        title={readOnly ? t("read_only_mode") : t("relabel_toggle_hint")}
                                    >
                                        {r.status === "available" ? t("relabel_status_available") : t("relabel_status_used")}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {!loading && total === 0 && (
                <div className="locations-table-empty">{t("relabel_no_rows")}</div>
            )}

            {total > 0 && (
                <div className="locations-table-footer">
                    <span>{t("locations_showing_range", {
                        from: String(rangeFrom),
                        to: String(rangeTo),
                        total: String(total),
                    })}</span>
                    <div className="locations-table-pagination">
                        <button
                            type="button"
                            className="locations-table-page-btn"
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            aria-label={t("pagination_previous")}
                        >
                            <ChevronLeft size={16} />
                            <span>{t("pagination_previous")}</span>
                        </button>
                        <span className="locations-table-page-indicator">
                            {t("pagination_page_x_of_y", {
                                page: String(safePage + 1),
                                total: String(totalPages),
                            })}
                        </span>
                        <button
                            type="button"
                            className="locations-table-page-btn"
                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={safePage >= totalPages - 1}
                            aria-label={t("pagination_next")}
                        >
                            <span>{t("pagination_next")}</span>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RelabelAdmin;
